import { useEffect, useState } from 'react'
import { isAfter } from 'date-fns'
import { Button, Icon } from '@pmi/pmi-iqos-ds-react'
import { analytics, goTo } from '@pmi/pmi-iqos-shared-react'

export const getInitialPropsMock = mock => {
  return (() => {
    return require(`./mocks/initial-props/${mock}.json`)
  })()
}

export const buildCardData = res => {
  const selected = res.selected
  const itemIndex = res.itemIndex
  const icon = res.icon
  const NumberExpression = res.NumberExpression
  const arrayOfSteps = res.arrayOfSteps
  const title = res.title
  const description = res.description
  const buttonText = res.buttonText
  const linkText = res.linkText
  return {
    selected,
    itemIndex,
    icon,
    NumberExpression,
    arrayOfSteps,
    title,
    description,
    buttonText,
    linkText
  }
}

export const buildProfileInfoData = profileCookieData => {
  return (
    profileCookieData && {
      hasDevicesRegistered: profileCookieData.cp === 'DR',
      tierName: profileCookieData.tn,
      tierExpirationDate: profileCookieData.ted,
      tierQoins: profileCookieData.tq,
      ownedQoins: profileCookieData.oq
    }
  )
}

export const buildTiersStaticInfo = tiers => {
  return (
    tiers &&
    tiers.map(tier => {
      return {
        type: tier.id,
        label: tier.label,
        cardPortrait: tier.card_p,
        cardLandscape: tier.card_l,
        darkMode: tier.theme === 'dark',
        url: tier.url
      }
    })
  )
}

export const getPaginationParams = (
  pageSize,
  pageIndex,
  firstParam = false
) => {
  return (
    (firstParam ? '?' : '&') +
    'pageIndex=' +
    pageSize +
    '&pageSize=' +
    pageIndex
  )
}

export const buildActionData = (actionConfigs, actionState, translations) => {
  const mergedActions = actionConfigs.map(config => {
    const buttons = []
    config.cta &&
      config.cta.t &&
      config.cta.u &&
      buttons.push({
        text: config.cta.t,
        onClickHandler: () => {
          analytics.clickProfileQoinsAction(config.ql)
          goTo(config.cta.u)
        },
        'data-testing': 'ProfileQoins_Button_EarningAction'
      })

    config.lm &&
      buttons.push({
        text: translations.tk_learnMore,
        onClickHandler: () => {
          goTo(config.lm)
        },
        variant: Button.variants.tertiary,
        icon: Icon.images.arrowRight,
        iconPosition: Button.iconPositions.right,
        'data-testing': 'ProfileQoins_Button_EarningLink'
      })
    const mappedAction = {
      icon: config.i,
      amount: config.qn,
      title: config.t,
      description: config.d,
      feature: config.feat,
      buttons,
      recurrence: config.af
    }
    const combinedData =
      actionState &&
      actionState.actions &&
      actionState.actions.filter(action => action.code === config.lid)
    if (
      combinedData &&
      combinedData.length === 1 &&
      combinedData[0].limit !== 0
    ) {
      const steps = Array(combinedData[0].limit)
        .fill()
        .map((elem, index) => combinedData[0].count > index)
      const validated = steps.filter(Boolean).length === steps.length
      return {
        ...mappedAction,
        steps: validated ? null : steps,
        validated
      }
    } else {
      return { ...mappedAction }
    }
  })
  return mergedActions
}

export const buildVideoData = (actionConfigsVideo, translations) => {
  const mergedActionsVideo = actionConfigsVideo.map(config => {
    const buttons = []
    config.u &&
      config.vc &&
      buttons.push({
        text: translations.tk_videoViewNow,
        onClickHandler: () => {
          window.dispatchEvent(
            new window.CustomEvent('playQoinsVideo', {
              detail: {
                videoCode: config.vc,
                src: config.u
              }
            })
          )
        },
        'data-testing': 'ProfileQoins_Button_VideosActions'
      })

    const mappedAction = {
      icon: config.i,
      amount: config.qn,
      title: config.t,
      description: config.d,
      feature: config.feat,
      buttons
    }
    return { ...mappedAction }
  })
  return mergedActionsVideo
}

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])
  return debouncedValue
}

export const dispatchFullyEarnedEvent = () => {
  window.dispatchEvent(new window.CustomEvent('loyaltyRewardFullyEarned'))
}

export const dispatchPartiallyEarnedEvent = () => {
  window.dispatchEvent(new window.CustomEvent('loyaltyRewardPartiallyEarned'))
}

export const QuestTemplateTypes = {
  select: 'select',
  photo: 'photo',
  comment: 'comment',
  photoComment: 'photoComment'
}

export const questStatusTypes = {
  closed: 'closed',
  allowed: 'allowed',
  noticeFailed: 'failed',
  moderation: 'moderation',
  cleared: 'cleared',
  comingSoon: 'comingSoon'
}

export const buildQuestInfo = (questConfig, questProperties) => {
  const definedTemplate = questConfig.template
  const buildData = (data, template, questProperties) => {
    const mappedData = {
      id:
        questProperties && questProperties.id && questProperties.id.toString(),
      commentBackground: questProperties && questProperties.commentBackground,
      editionId: questProperties && questProperties.editionId,
      title: data.title,
      footerTitle: data.footer,
      terms:
        data.terms &&
        data.terms.map(option => {
          return {
            name: option.text,
            mandatory: !option.mandatory,
            checked: option.checked
          }
        }),
      components:
        data.inputs &&
        data.inputs.map(component => {
          return {
            id: component.id.toString(),
            type: component.type,
            ...(template === QuestTemplateTypes.select
              ? {
                  maxSelection: component.maxItems,
                  ...(component.items && {
                    options: component.items.map(option => {
                      return {
                        id: option.id.toString(),
                        label: option.text,
                        image: option.image,
                        selected: option.selected
                      }
                    })
                  })
                }
              : {
                  heading: component.heading,
                  label: component.label,
                  value: '',
                  photo: ''
                })
          }
        }),
      hasImages:
        data.inputs &&
        data.inputs[0] &&
        data.inputs[0].items &&
        data.inputs[0].items.filter(option => option.image).length > 0
    }
    return mappedData
  }

  const mappedProperties = {
    id: questProperties && questProperties.id,
    commentBackground: questProperties && questProperties.commentBackground,
    editionId: questProperties && questProperties.edition_id
  }
  const mappedData = buildData(questConfig, definedTemplate, mappedProperties)
  return mappedData
}

export const buildQuestDetailState = questDetail => {
  if (questDetail && questDetail.quest && questDetail.quest.type === 'post') {
    return {
      isPost: true,
      photo: questDetail && questDetail.share && questDetail.share.image,
      comment: questDetail && questDetail.share && questDetail.share.value,
      date: questDetail && questDetail.share && questDetail.share.date,
      template: questDetail && questDetail.quest && questDetail.quest.template
    }
  } else {
    return (
      questDetail &&
      questDetail.quest &&
      questDetail.quest.answers &&
      questDetail.quest.answers.map(answer => {
        return {
          id: answer.id.toString(),
          value: answer.answer,
          date: answer.date
        }
      })
    )
  }
}

export const questDateConditions = ({
  currentDate,
  startDate,
  openDate,
  closeDate
}) => {
  return {
    isStartInPast: isAfter(currentDate, new Date(startDate)),
    isOpenInpast: isAfter(currentDate, new Date(openDate)),
    isCloseInFuture: isAfter(new Date(closeDate), currentDate)
  }
}

export const buildQuestState = (res, noticeStatus) => {
  let status = null
  const { closeDate, startDate, openDate } = res
  const currentDate = new Date()
  const { isStartInPast, isOpenInpast, isCloseInFuture } = questDateConditions({
    currentDate,
    startDate,
    openDate,
    closeDate
  })

  if (isStartInPast && isOpenInpast && isCloseInFuture) {
    switch (res.status) {
      case 1:
        status = questStatusTypes.allowed
        break
      case 2:
        status = questStatusTypes.moderation
        break
      case 3:
        status = questStatusTypes.cleared
        break
    }
  } else if (isStartInPast && !isOpenInpast && isCloseInFuture) {
    switch (res.status) {
      case 0:
        status = questStatusTypes.comingSoon
        break
    }
  } else if (isStartInPast && isOpenInpast && !isCloseInFuture) {
    switch (res.status) {
      case 0:
        status = questStatusTypes.closed
        break
    }
  } else {
    switch (res.status) {
      case 0:
        status = questStatusTypes.closed
        break
      case 1:
        status = questStatusTypes.allowed
        break
      case 2:
        status = questStatusTypes.moderation
        break
      case 3:
        status = questStatusTypes.cleared
        break
    }
  }

  if (noticeStatus === 0) {
    status = questStatusTypes.noticeFailed
  }

  return {
    id: res.id,
    group: res.group,
    template: res.template,
    type: res.type,
    campaign: res.campaign,
    campaign2: res.campaign2,
    qoins: res.qoins,
    bonus: res.bonus,
    status: status,
    closed: res.closed,
    content: res.content ? { ...res.content } : {},
    answers: res.answers ? [...res.answers] : [],
    answers2: res.answers2 ? [...res.answers2] : [],
    openDate
  }
}

export const mapPrizeToProduct = product => {
  const allOptions = product.options || []
  if (product) {
    const filteredOptions =
      allOptions.filter(
        (option, index, arr) =>
          arr.map(o => o.hexColor).indexOf(option.hexColor) === index
      ) || []
    return {
      product: {
        productId: product.productId,
        name: product.name,
        seoTitle: product.seoTitle,
        seoDescription: product.seoDescription,
        selectedOptionIndex:
          product.selectedVariant >= 0 ? product.selectedVariant : 0,
        loyalty: {
          section: product.loyaltySection,
          sizeGuide: product.fullDescription,
          name: product.name,
          pdpCta: product.secondaryCTA,
          extraCta: product.tertiaryCTA
        },
        options: filteredOptions.map(option => ({
          ...option,
          color: option.hexColor,
          price: parseFloat(option.price),
          listPrice: parseFloat(option.list_price),
          description: option.fullDescription,
          shortVariantName: option.shortVariantName,
          qoins: parseInt(option.loyaltyPoints, 10),
          images: option.images.map(image => ({
            ...image,
            path: image.imagePath
          })),
          media:
            option &&
            option.media &&
            option.media.map(item => {
              if (item.type === 'video') {
                return {
                  ...item,
                  source: item.path
                }
              }
              return {
                ...item
              }
            }),
          hasStockDigitalVouchers: !!(
            option.stockDigitalVouchers &&
            parseInt(option.stockDigitalVouchers, 10) > 0
          ),
          loyalty: {
            sizes:
              allOptions
                .filter(opt => opt.hexColor === option.hexColor)
                .map(dupOpt => ({
                  id: dupOpt.prizeCode,
                  value: dupOpt.size || ''
                })) || null,
            disclaimerImage: option.disclaimer
          },
          badges:
            option.badges &&
            option.badges.map(badge => ({
              background_colour: badge.backgroundColour,
              font_colour: badge.fontColour,
              title: badge.title
            }))
        })),
        campaignUrl: product.campaignUrl,
        isDigital: !!product.isDigital
      }
    }
  }
}
